import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';

interface DeactivateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  username: string;
  displayName: string;
}

export const DeactivateDialog: React.FC<DeactivateDialogProps> = ({
  open,
  onOpenChange,
  username,
  displayName,
}) => {
  const [loading, setLoading] = useState(false);

  const { deactivateUserAccount, showSuccess, showError } = useStore(
    useShallow(state => ({
      deactivateUserAccount: state.deactivateUserAccount,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const handleDeactivate = async () => {
    setLoading(true);
    try {
      await deactivateUserAccount(username);
      showSuccess(`User ${displayName} has been deactivated`);
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to deactivate user';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deactivate User</DialogTitle>
          <DialogDescription>
            Are you sure you want to deactivate <strong>{displayName}</strong>?
            This action will prevent them from accessing the system.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type='button'
            variant='destructive'
            onClick={handleDeactivate}
            disabled={loading}
          >
            {loading ? 'Deactivating...' : 'Deactivate User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
